import { ClientCreation, ClientRaw } from '@Arti-zen/package-backoffice'
import { useAppSelector } from 'hooks/useStore'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { sendClientsRawByChunk } from '../clients.helper'
import { pages } from 'navigation/pages'
import { Button, CircularProgress, Dialog } from '@mui/material'
import styled from 'styled-components'
import { Theme } from 'theme/theme'
import ExempleClient from './ExempleClient'

type TProps = {
  extraColumns: string[]
  clientsRaw: ClientRaw[]
  onModify: boolean
  onClose: () => void
}

const prettifyColumns = {
  lastName: 'Nom',
  firstName: 'Prénom',
  phone: 'Téléphone fixe',
  mobile: 'Téléphone mobile',
  email: 'Email',
  address: 'Adresse',
  additionalAddress: "Complément d'adresse",
  id: 'ID Client',
  postalCode: 'Code postal',
  city: 'Ville',
  siren: 'N° de SIRET',
  isProfessional: 'Client professionnel',
  vatNumber: 'N° de TVA',
  title: 'Civilité',
  clientNotes: 'Notes client',
}

const reversePrettifyColumns = Object.fromEntries(Object.entries(prettifyColumns).map(([key, value]) => [value, key]))

export default function ErrorColumnsPopup({ extraColumns, clientsRaw, onModify, onClose }: TProps) {
  const { artisan } = useAppSelector((state) => state.artisan)

  const expectedColumns = [
    onModify ? 'id' : null,
    'lastName',
    'firstName',
    'phone',
    'mobile',
    'email',
    'address',
    'additionalAddress',
    'postalCode',
    'city',
    'title',
    'isProfessional',
    'siren',
    'clientNotes',
  ].filter(Boolean)

  const currentColumns = Object.keys(clientsRaw[0])

  const [extra, setExtra] = useState<Set<string>>(new Set(extraColumns))
  const [zones, setZones] = useState<Record<string, Set<string>>>(
    Object.fromEntries(
      expectedColumns.map((column) => [
        column,
        currentColumns.includes(column) || currentColumns.includes(prettifyColumns[column])
          ? new Set([prettifyColumns[column] ?? column])
          : new Set(),
      ])
    )
  )

  const [availableOnSubmit, setAvailableOnSubmit] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const navigate = useNavigate()

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, item: string) => {
    event.dataTransfer.setData('text/plain', item)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, zoneId: string) => {
    event.preventDefault()
    setAvailableOnSubmit(false)

    const item = event.dataTransfer.getData('text/plain')

    if (!item) return

    if (extra.has(item) && zoneId === 'extra') {
      return
    }

    if (zoneId === 'extra') {
      const newExtra = new Set([...extra])
      newExtra.add(item)
      setExtra(newExtra)
    }
    setZones((previous) => {
      const newZones = { ...previous }

      Object.keys(newZones).forEach((zone) => {
        if (newZones[zone].has(item)) newZones[zone].delete(item)
      })

      if (zoneId !== 'extra') newZones[zoneId].add(item)
      return newZones
    })

    if (extra.has(item)) {
      const newExtra = new Set([...extra])
      newExtra.delete(item)
      setExtra(newExtra)
    }
  }

  const onValid = () => {
    if (zones.lastName.size === 0 || (onModify && zones.id.size === 0)) {
      setErrorMessage(
        onModify
          ? 'Les colonnes "id" et "Nom" doivent avoir au moins une propriété'
          : `La colonne "Nom" doit avoir au moins une propriété.`
      )

      return
    }
    setErrorMessage(null)
    setAvailableOnSubmit(true)
  }

  const onSubmit = async () => {
    setIsLoading(true)
    const newClients: ClientRaw[] = clientsRaw.map((clientRaw) => {
      const client = {}

      expectedColumns.forEach((prop) => {
        client[prop] = [...zones[prop]]
          .map(
            (column) =>
              clientRaw?.[prettifyColumns[column]] ?? clientRaw?.[reversePrettifyColumns[column]] ?? clientRaw?.[column]
          )
          .filter(Boolean)
          .join(' - ')
          .trim()
      })

      return client as ClientRaw
    })

    const result = await sendClientsRawByChunk({ clientsToSend: newClients, companyId: String(artisan.id), onModify })
    setIsLoading(false)

    if (result.success === false) {
      setErrorMessage(result.error)
    } else {
      navigate(pages.connected.artisan.uploadClients.navigate(+artisan.id), { state: { result } })
    }
  }

  return (
    <Dialog open fullWidth maxWidth="xl" onClose={onClose}>
      <Container
        style={
          isLoading
            ? { color: Theme.colors.blueArtizen, justifyContent: 'center', alignItems: 'center', display: 'flex' }
            : {}
        }
      >
        {isLoading ? (
          <CircularProgress color="inherit" />
        ) : (
          <>
            <div>
              <p>Colonnes reçues</p>
              <p style={{ color: 'darkgray', fontSize: 13 }}>
                Toutes les colonnes laissées dans cet espace seront ignorées.
              </p>
              <Zone
                key={'zone-extra'}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => handleDrop(event, 'extra')}
              >
                <Row style={{ flexWrap: 'wrap', minHeight: 33 }}>
                  {[...extra].map((column) => (
                    <Draggable
                      key={`draggable-${column}`}
                      draggable
                      onDragStart={(event) => handleDragStart(event, column)}
                    >
                      {column}
                    </Draggable>
                  ))}
                </Row>
              </Zone>
            </div>
            <div>
              <p>Colonnes attendues</p>
              <Row>
                {Object.entries(zones).map(([zoneId, zoneItems]) => (
                  <Zone
                    key={`zone-${zoneId}`}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => handleDrop(event, zoneId)}
                  >
                    <h3>{prettifyColumns[zoneId]}</h3>
                    {[...zoneItems].map((item) => (
                      <Draggable
                        key={`dragged-${item}`}
                        draggable
                        onDragStart={(event) => handleDragStart(event, item)}
                      >
                        {item}
                      </Draggable>
                    ))}
                  </Zone>
                ))}
              </Row>
            </div>

            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}

            {availableOnSubmit && (
              <ExempleClient
                clients={clientsRaw.slice(0, 5)}
                newHeaders={zones}
                expectedColumns={expectedColumns as (keyof ClientCreation)[]}
                prettifyColumns={prettifyColumns}
              />
            )}

            <Button
              onClick={availableOnSubmit ? onSubmit : onValid}
              style={{
                display: 'flex',
                alignSelf: 'center',
                backgroundColor: Theme.colors.blueArtizen,
                color: 'white',
                width: '100%',
                marginTop: 20,
              }}
            >
              Valider
            </Button>
          </>
        )}
      </Container>
    </Dialog>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  gap: 40px;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex: 1;
  flex-wrap: wrap;
`

const Draggable = styled.div`
  text-align: center;
  color: ${Theme.colors.blueArtizen};
  border: 1px solid ${Theme.colors.blueArtizen};
  padding: 8px 16px;
  border-radius: 6px;
  background-color: #f6f6f6;
  font-size: 13px;
  font-weight: 500;
  cursor: grab;
`

const Zone = styled.div`
  background-color: #0a30af10;
  border-radius: 5px;
  border: 2px dashed #0a30af;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  text-align: center;
  min-width: 13%;
  h3 {
    margin: 5px 0 5px 0;
    font-size: 15px;
  }
`
